import {
  useMutation,
  useSuspenseQuery,
  UseSuspenseQueryOptions,
} from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';

import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';
import { Partner } from '@repo/types/Partner';
import { API } from '~/api';

// Server function to get the current partner
export const getCurrentPartner = createServerFn({
  method: 'GET',
}).handler<Partner | null>(async () => {
  const response = await API.get('/api/partners/me', {}, true, undefined, {
    tags: {
      section: SECTIONS.AUTH,
      domain: DOMAINS.PARTNERS,
      importance: IMPORTANCE.CRITICAL,
    },
    context: {},
  });
  return response?.result ?? null;
});

export const currentPartnerQueryOptions = (
  isAuthenticated: boolean,
): UseSuspenseQueryOptions<Partner | null, Error> => ({
  queryKey: ['currentPartner'],
  queryFn: async () => (isAuthenticated ? await getCurrentPartner() : null),
});

export const updateCurrentPartner = createServerFn({
  method: 'POST',
  response: 'data',
})
  .validator((partner: Partial<Partner>) => partner)
  .handler(async ({ data }) => {
    try {
      const response = await API.put(
        '/api/partners/me',
        data,
        true,
        undefined,
        {
          tags: {
            section: SECTIONS.SETTINGS,
            domain: DOMAINS.PARTNERS,
            importance: IMPORTANCE.MEDIUM,
          },
          context: {},
        },
      );

      return response?.result;
    } catch (error: any) {
      if (error?.response?.status) {
        const err = new Error(error?.response?.data?.message || error.message);
        (err as any).status = error.response.status;
        throw err;
      }
      throw error;
    }
  });

export const rotatePartnerApiKey = createServerFn({
  method: 'POST',
  response: 'data',
}).handler(async () => {
  try {
    const response = await API.post(
      '/api/partners/api-key',
      {},
      true,
      undefined,
      {
        tags: {
          section: SECTIONS.SETTINGS,
          domain: DOMAINS.PARTNERS,
          importance: IMPORTANCE.MEDIUM,
        },
        context: {},
      },
    );

    return response;
  } catch (error: any) {
    if (error?.response?.status) {
      const err = new Error(error?.response?.data?.message || error.message);
      (err as any).status = error.response.status;
      throw err;
    }
    throw error;
  }
});

export const upsertPartnerWebhookUrl = createServerFn({
  method: 'POST',
  response: 'data',
})
  .validator((url: string) => url)
  .handler(async ({ data: url }) => {
    try {
      const response = await API.put(
        '/api/partners/hooks/destination',
        { url },
        true,
        undefined,
        {
          tags: {
            section: SECTIONS.SETTINGS,
            domain: DOMAINS.PARTNERS,
            importance: IMPORTANCE.HIGH,
          },
          context: {},
        },
      );
      return response?.result;
    } catch (error: any) {
      if (error?.response?.status) {
        const err = new Error(error?.response?.data?.message || error.message);
        (err as any).status = error.response.status;
        throw err;
      }
      throw error;
    }
  });

export const rotatePartnerWebhookSecret = createServerFn({
  method: 'POST',
  response: 'data',
}).handler(async () => {
  try {
    const response = await API.post(
      '/api/partners/hooks/secret',
      {},
      true,
      undefined,
      {
        tags: {
          section: SECTIONS.SETTINGS,
          domain: DOMAINS.PARTNERS,
          importance: IMPORTANCE.HIGH,
        },
        context: {},
      },
    );

    return response;
  } catch (error: any) {
    if (error?.response?.status) {
      const err = new Error(error?.response?.data?.message || error.message);
      (err as any).status = error.response.status;
      throw err;
    }
    throw error;
  }
});

export const useCurrentPartner = () => {
  const isAuthenticated = useRouteContext({ from: '__root__' }).isAuthenticated;
  const { data: partner, isLoading } = useSuspenseQuery<Partner | null>(
    currentPartnerQueryOptions(isAuthenticated),
  );
  return { partner, isLoading };
};

export const useUpdateCurrentPartner = () => {
  const { queryClient } = useRouteContext({
    from: '__root__',
  });

  return useMutation({
    mutationFn: ({ partner }: { partner: Partial<Partner> }) =>
      updateCurrentPartner({ data: partner }),
    onMutate: async ({ partner }) => {
      await queryClient.cancelQueries({ queryKey: ['currentPartner'] });

      const previous = queryClient.getQueryData<Partner | null>([
        'currentPartner',
      ]);

      if (previous) {
        const next: Partner = {
          ...previous,
          ...partner,
          portal_config:
            partner.portal_config !== undefined
              ? {
                  ...(previous.portal_config ?? {}),
                  ...(partner.portal_config ?? {}),
                }
              : previous.portal_config,
        };

        queryClient.setQueryData(['currentPartner'], next);
      }

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['currentPartner'], context.previous);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentPartner'] });
    },
  });
};

export const useRotatePartnerApiKey = () => {
  const { queryClient } = useRouteContext({
    from: '__root__',
  });

  return useMutation({
    mutationFn: async () => {
      const response = await rotatePartnerApiKey();
      return (response as any)?.apiKey as string | undefined;
    },
    onSuccess: () => {
      queryClient.setQueryData<Partner | null>(['currentPartner'], prev => {
        if (!prev) return prev;
        return { ...prev, has_api_key: true };
      });
      queryClient.invalidateQueries({ queryKey: ['currentPartner'] });
    },
  });
};

export const useUpsertPartnerWebhookUrl = () => {
  const { queryClient } = useRouteContext({
    from: '__root__',
  });

  return useMutation({
    mutationFn: (url: string) => upsertPartnerWebhookUrl({ data: url }),
    onSuccess: (_data, url) => {
      queryClient.setQueryData<Partner | null>(['currentPartner'], prev => {
        if (!prev) return prev;
        return { ...prev, webhook_url: url };
      });

      queryClient.invalidateQueries({ queryKey: ['currentPartner'] });
    },
  });
};

export const useRotatePartnerWebhookSecret = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await rotatePartnerWebhookSecret();
      return (response as any)?.secret as string | undefined;
    },
  });
};
